// G360_K462 - a failed read is said, not shown as an empty list or a zero; a refused change is said, not shown as done.
// Platform overview - the whole business at a glance.
// G360_K482 - the tenant list is the shared DataTable (sort, filter, column
// picker, CSV export). A tenant with no subscription status is shown as
// "none recorded", not "trialing" - the old fallback invented a state.

import { useEffect, useState } from 'react';
import { Building2, Users, Radar, AlertOctagon, Crown, Sparkles } from 'lucide-react';
import { useAuth } from '@govern360/shared';
import { listTenants, tenantStats, seedAdminDemo } from '../lib/adminApi.js';
import { DataTable } from '@govern360/shared/components/datatable.jsx';

export default function OverviewPage() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState({ tools: null, agents: null, incidents: null });
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoading(true);
    const [{ data, error }, s] = await Promise.all([listTenants(), tenantStats()]);
    setLoadErr(error ? (error.message || 'the read failed') : '');
    setTenants(error ? [] : data); setStats(s);
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function bootstrap() {
    setBusy(true);
    const { error } = await seedAdminDemo();
    setBusy(false);
    if (error) { alert('Bootstrap failed: ' + (error.message || 'refused')); return; }
    alert('Bootstrapped: you are a super-admin and sample feature flags were added.');
    refresh();
  }

  const activeSubs = tenants.filter((t) => t.subscription_status === 'active').length;
  const day = (v) => { const ms = Date.parse(v || ''); return isNaN(ms) ? null : ms; };
  const tenantCols = [
    { key: 'name', label: 'Tenant', value: (t) => t.name || '', render: (t) => <strong>{t.name}</strong> },
    { key: 'industry', label: 'Industry', value: (t) => t.industry || '' },
    { key: 'plan', label: 'Plan', value: (t) => t.plan || '', render: (t) => (t.plan ? <span className="pill p-info">{t.plan}</span> : null) },
    { key: 'status', label: 'Status', value: (t) => t.subscription_status || '',
      csv: (t) => t.subscription_status || 'none recorded',
      render: (t) => (t.subscription_status
        ? <span className={`pill ${t.subscription_status === 'active' ? 'p-succ' : 'p-gray'}`}>{t.subscription_status}</span>
        : <span style={{ fontSize: 12, color: 'var(--g-text-3)' }} title="No subscription status is recorded for this tenant">none recorded</span>) },
    { key: 'seats', label: 'Seats', align: 'right', value: (t) => (t.seats == null ? null : Number(t.seats)) },
    { key: 'joined', label: 'Joined', value: (t) => String(t.created_at || '').slice(0, 10), sort: (t) => day(t.created_at),
      render: (t) => (day(t.created_at) == null ? null : <span style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{new Date(t.created_at).toLocaleDateString()}</span>) },
  ];

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Platform overview</h1>
          <p className="ph-sub">All tenants, all metrics. The whole business at a glance.</p>
        </div>
        <div className="ph-cta">
          <button className="btn outline" onClick={bootstrap} disabled={busy}><Sparkles size={18} />Bootstrap demo</button>
        </div>
      </div>

      <div className="banner admin">
        <Crown size={18} />
        <span>Signed in as <strong>{user?.name} &middot; Super admin</strong>. Every action is recorded in the cross-tenant audit log.</span>
      </div>

      <div className="row r4" style={{ marginBottom: 16 }}>
        <div className="card"><p className="metric-l"><Building2 size={16} />Tenants</p><p className="metric-v">{loadErr ? '?' : tenants.length}</p></div>
        <div className="card"><p className="metric-l"><Radar size={16} />AI tools governed</p><p className="metric-v">{stats.tools == null ? '?' : stats.tools.toLocaleString()}</p></div>
        <div className="card"><p className="metric-l"><Users size={16} />Agents tracked</p><p className="metric-v">{stats.agents == null ? '?' : stats.agents.toLocaleString()}</p></div>
        <div className="card"><p className="metric-l"><AlertOctagon size={16} />Open incidents</p><p className="metric-v">{stats.incidents == null ? '?' : stats.incidents.toLocaleString()}</p></div>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading&hellip;</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>Tenants could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.</p>
        </div>
      ) : (
        <div className="card pad-0">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--g-divider)', fontSize: 13, fontWeight: 500 }}>
            Tenants ({tenants.length}) &middot; {activeSubs} active subscriptions
          </div>
          <div style={{ padding: '10px 12px' }}>
            <DataTable rows={tenants} columns={tenantCols} card={false} tableClass="g360-admin-ov-tb"
              storageKey="g360.admin.overview.hiddenCols" name="tenants" placeholder="Filter tenants..."
              rowKey={(t, i) => t.id || i} />
          </div>
        </div>
      )}
    </div>
  );
}
